import type { StorefrontBrand } from '@erp/shared';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { publicApi } from '../../lib/publicApi.js';

import { HomeSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';

/** Browse-all-brands index — a grid of every brand that has published products. */
export function StorefrontBrandsPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const [brands, setBrands] = useState<StorefrontBrand[] | null>(null);

  useEffect(() => {
    setBrands(null);
    void publicApi().get<{ items: StorefrontBrand[] }>(`/${slug}/brands`).then((r) => setBrands(r.data.items)).catch(() => setBrands([]));
  }, [slug, i18n.language]);

  return (
    <div className="flex flex-col gap-6">
      <Helmet><title>{`${t('store.brands.title', { defaultValue: 'Brands' })} · ${site.storeName}`}</title></Helmet>
      <header>
        <h1 className="text-3xl font-bold tracking-tight">{t('store.brands.title', { defaultValue: 'Brands' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('store.brands.subtitle', { defaultValue: 'Shop by your favorite brands.' })}</p>
      </header>

      {!brands ? (
        <HomeSkeleton />
      ) : brands.length === 0 ? (
        <div className="py-16 text-center text-fg-muted">{t('store.brands.empty', { defaultValue: 'No brands yet.' })}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {brands.map((b) => {
            return (
              <Link key={b.id} to={`/s/${slug}/brand/${b.id}`} className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-bg-subtle p-5 text-center transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg active:scale-[0.99] motion-reduce:transform-none">
                <div className="grid aspect-square w-full place-items-center overflow-hidden rounded-xl bg-white p-3">
                  <StoreImage src={b.imageUrl} alt={b.name} className="max-h-full max-w-full object-contain transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transform-none" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{b.name}</div>
                  <div className="text-xs text-fg-muted">{t('store.category.count', { count: b.productCount })}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
